import * as React from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { usePostHog } from 'posthog-js/react'
import { toast } from 'sonner'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  classifyFetchError,
  SERVER_UNREACHABLE_MESSAGE,
  UPLOAD_BLOCKED_MESSAGE,
} from '@/lib/fetchError'
import { retryOnNetworkError } from '@/lib/retryOnNetworkError'
import { useUploadedFilesQuery } from '@/features/files/hooks/useUploadedFilesQuery'
import {
  isUploadBackendReachable,
  MAX_UPLOAD_BYTES,
  uploadFile,
} from '@/features/files/api'

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function formatTimestamp(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString()
}

export function FilesPage() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const posthog = usePostHog()
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [uploading, setUploading] = React.useState(false)
  const [uploadError, setUploadError] = React.useState<string | null>(null)
  const filesQuery = useUploadedFilesQuery({ limit: 25 })
  const files = React.useMemo(
    () => filesQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [filesQuery.data]
  )

  const handleFileSelected = React.useCallback((file: File | null) => {
    setUploadError(null)
    if (!file) {
      setSelectedFile(null)
      return
    }
    if (file.size === 0) {
      setUploadError('File cannot be empty')
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError('File size must be less than 30MB')
      return
    }
    setSelectedFile(file)
  }, [])

  const handleUpload = React.useCallback(async () => {
    if (!selectedFile) return
    setUploading(true)
    setUploadError(null)

    try {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      await retryOnNetworkError(() => uploadFile(token, selectedFile))
      toast.success('File uploaded successfully')
      setSelectedFile(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      await queryClient.invalidateQueries({ queryKey: ['uploaded-files'] })
    } catch (error) {
      const kind = classifyFetchError(error)
      console.error('File upload failed', error)

      // A network-layer failure means the upload never reached the server.
      // Probe a known-good GET to tell "server unreachable" apart from "this
      // upload was blocked before it was sent" (e.g. an extension/DLP/firewall).
      let backendReachable: boolean | undefined
      let message: string
      if (kind === 'network') {
        const token = await getToken().catch(() => null)
        backendReachable = token ? await isUploadBackendReachable(token) : false
        message = backendReachable
          ? UPLOAD_BLOCKED_MESSAGE
          : SERVER_UNREACHABLE_MESSAGE
      } else {
        const reason =
          error instanceof Error ? error.message : 'Something went wrong.'
        message = `Couldn't upload ${selectedFile.name}: ${reason}`
      }

      posthog?.captureException(error, {
        context: 'file_upload',
        endpoint: '/api/data/files',
        method: 'POST',
        file_name: selectedFile.name,
        file_size: selectedFile.size,
        online: navigator.onLine,
        kind,
        backend_reachable: backendReachable,
      })
      setUploadError(message)
      toast.error(message)
    } finally {
      setUploading(false)
    }
  }, [getToken, queryClient, selectedFile, posthog])

  return (
    <div className="h-full overflow-y-auto p-4 space-y-6">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold">Files</h2>
        <p className="text-sm text-muted-foreground">
          Drop files here to upload them to storage.
        </p>
      </div>

      <div className="space-y-3">
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragging
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragging(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragging(false)
          }}
          onDrop={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setDragging(false)
            handleFileSelected(e.dataTransfer.files?.[0] ?? null)
          }}
          onClick={() => fileInputRef.current?.click()}>
          <Input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => handleFileSelected(e.target.files?.[0] ?? null)}
            disabled={uploading}
          />
          <Upload className="mx-auto h-7 w-7 text-gray-400 mb-2" />
          {selectedFile ? (
            <div>
              <p className="text-sm font-medium">{selectedFile.name}</p>
              <p className="text-xs text-gray-500">
                {formatSize(selectedFile.size)}
              </p>
            </div>
          ) : (
            <div>
              <p className="text-sm">
                Drag and drop a file, or click to browse
              </p>
              <p className="text-xs text-gray-500">Maximum 30MB</p>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          {uploadError && <p className="text-sm text-red-500">{uploadError}</p>}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-base font-semibold">Previously uploaded files</h3>

        {filesQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading files…</p>
        ) : filesQuery.isError ? (
          <p className="text-sm text-red-500">
            {filesQuery.error instanceof Error
              ? filesQuery.error.message
              : 'Failed to load files'}
          </p>
        ) : files.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No files uploaded yet.
          </p>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-2">Filename</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Uploaded at</th>
                  <th className="text-left p-2">Storage path</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.id} className="border-t">
                    <td className="p-2">{file.filename}</td>
                    <td className="p-2">{file.status || '—'}</td>
                    <td className="p-2">{formatTimestamp(file.created_at)}</td>
                    <td className="p-2 break-all">{file.storage_path}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filesQuery.hasNextPage && (
          <Button
            variant="outline"
            onClick={() => filesQuery.fetchNextPage()}
            disabled={filesQuery.isFetchingNextPage}>
            {filesQuery.isFetchingNextPage ? 'Loading...' : 'Load more'}
          </Button>
        )}
      </div>
    </div>
  )
}
